import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadOrganizationProps,OrganizationUsersGridParams, LoadOrganizationsProps, OrganizationGridParams, ReduxOrganizationsSearchState, UpdateOrganizationProps, ViewOrganizationProps } from "../types";
import { ISelectItem, ReduxSliceState } from "../../../shared/types";
import { OrganizationItem, UserItem } from "../../../api/types";
import { ReduxRootState } from "../../../shared/redux/store";
import { withPayloadType } from "../../../shared/utils";

const initialState: ReduxOrganizationsSearchState = {
    state: 'BUSY',
    organizationGridParams: {
        sortCol: 'orgId',
        sortDir: 'desc',
        search: undefined,
        status: true,
        limit: 500,
        previousPage: undefined,
        nextPage: undefined,
    },
    organizationUsersGridParams: {
        active: true,
    },
    prevPage: undefined,
    nextPage: undefined,
    organizations: [],
    selectedOrganizationUsersState: 'BUSY',
    organizationUsers: [],
    selectedOrganization: undefined,
    selectedOrganizationRowIndex: undefined,
};

export const loadOrganizations = createAction('organizations/loadOrganizations', withPayloadType<void>());
export const filterOrganizations = createAction('organizations/search/filterOrders', withPayloadType<LoadOrganizationsProps>());
export const loadOrganizationUsers = createAction('organizations/loadUsers', withPayloadType<string | number | undefined>());
export const loadOrganization = createAction('organizations/loadOrganization', withPayloadType<string | number>());
export const updateOrganization = createAction('organizations/updateOrganization', withPayloadType<UpdateOrganizationProps>());
export const viewOrganization = createAction('organizations/viewOrganization', withPayloadType<ViewOrganizationProps>());

const organizationsSearchSlice = createSlice({
    name: 'organizations/search',
    initialState,
    reducers: {
        setState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.state = action.payload;
        },
        setOrganizations: (state, action: PayloadAction<OrganizationItem[]>) => {
            state.organizations = action.payload;
        },
        setOrganizationUsersState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.selectedOrganizationUsersState = action.payload;
        },
        setOrganizationUsers: (state, action: PayloadAction<UserItem[]>) => {
            state.organizationUsers = action.payload;
        },
        setSelectedOrganization: (state, action: PayloadAction<OrganizationItem | undefined>) => {
            state.selectedOrganization = action.payload;
        },
        setSelectedOrganizationRowIndex: (state, action: PayloadAction<number | undefined>) => {
            state.selectedOrganizationRowIndex = action.payload;
        },
        updateGridParams: (state, action: PayloadAction<Partial<OrganizationGridParams>>) => {
            state.organizationGridParams = {
                ...state.organizationGridParams,
                ...action.payload,
            };
        },
        updateOrganizationUsersParams: (state, action: PayloadAction<Partial<OrganizationUsersGridParams>>) => {
            state.organizationUsersGridParams = {
                ...state.organizationUsersGridParams,
                ...action.payload,
            };
        },
    }
});

const selectSelf = (state: ReduxRootState) => state.organizationsSearch;
export const selectOrganizationsSearchState = createSelector(selectSelf, self => self.state);
export const selectOrganizationsSearchPageBusy = createSelector(selectOrganizationsSearchState, state => state === 'BUSY');
export const selectOrganizationsSearchOrganizationsGridParams = createSelector(selectSelf, self => self.organizationGridParams);
export const selectOrganizationsSearchOrganizationsGridParamsSearch = createSelector(selectOrganizationsSearchOrganizationsGridParams, params => params.search);
export const selectOrganizationsSearchOrganizationsGridParamsStatus = createSelector(selectOrganizationsSearchOrganizationsGridParams, params => params.status);
export const selectOrganizationsSearchOrganizations = createSelector(selectSelf, self => self.organizations);
export const selectOrganizationsSearchSelectedOrganization = createSelector(selectSelf, self => self.selectedOrganization);
export const selectOrganizationsSearchSelectedOrganizationId = createSelector(selectOrganizationsSearchSelectedOrganization, org => org?.orgId);
export const selectOrganizationsSearchSelectedOrganizationName = createSelector(selectOrganizationsSearchSelectedOrganization, org => org?.name);
export const selectOrganizationsSearchSelectedOrganizationSuspendStatus = createSelector(selectOrganizationsSearchSelectedOrganization, selectedOrganization => selectedOrganization?.suspend);
export const selectOrganizationsSearchSelectedOrganizationShipDirectStatus = createSelector(selectOrganizationsSearchSelectedOrganization, selectedOrganization => selectedOrganization?.shipDirect);
export const selectOrganizationsSearchSelectedOrgRowIndex = createSelector(selectSelf, self => self.selectedOrganizationRowIndex);
export const selectOrganizationsSearchUsersState = createSelector(selectSelf, self => self.selectedOrganizationUsersState);
export const selectOrganizationsSearchUsersBusy = createSelector(selectOrganizationsSearchUsersState, state => state === 'BUSY');
export const selectOrganizationsSearchSelectedOrganizationUsers = createSelector(selectSelf, self => self.organizationUsers);
export const selectOrganizationsSearchSelectedOrganizationUserNamesList = createSelector(selectOrganizationsSearchSelectedOrganizationUsers, organizationUsers => organizationUsers.map<ISelectItem>(user => ({
    label: user.name!,
    value: user.name!,
})));
export const selectOrganizationsSearchSelectedOrganizationUsersGridParams = createSelector(selectSelf, self => self.organizationUsersGridParams);
export const selectOrganizationsSearchSelectedOrganizationUsersGridParamsActive = createSelector(selectOrganizationsSearchSelectedOrganizationUsersGridParams, params => params.active);
export default organizationsSearchSlice;
